import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import ConfirmModal from "../shared/Confirm/ConfirmModal";
import Modal from "../../layouts/Modal";
import WorkflowStepper, { WorkflowStep, WorkflowStepState } from "../shared/Workflow/WorkflowStepper";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../notifications/useNotifications";
import useCheckIn from "../../data/useCheckIn";
import useJobsByVehicle from "../../data/useJobsByVehicle";
import useDepollution from "../../data/useDepollution";
import useTriage from "../../data/useTriage";
import { IVehicle } from "../../models/Vehicle";
import Depollution from "../../pages/Depollution/Depollution";
import Triage from "../../pages/Triage/Triage";
import { IJob } from "../../models/Job";
import LeadCheckInModal from "../leads/LeadInformation/LeadCheckInModal";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";

function isJobDismantlingDone(job: IJob) {
  if (job.completedOn) return true;
  if (!job.jobPartCategories || job.jobPartCategories.length === 0) return false;

  return job.jobPartCategories.every((category) => {
    const part = category.partData;
    if (!part) return true;

    const isPartDeleted = !!(part as any).deleted;
    const isPartRemoved = !!part.removedOn;
    const isPartDamaged = !!part.isDamaged;

    return isPartDeleted || isPartRemoved || isPartDamaged;
  });
}

export default function VehicleWorkflowCard({
  vehicle,
  createJobsTargetId,
}: {
  vehicle: IVehicle;
  createJobsTargetId: string;
}) {
  const { t } = useTranslation("vehicles");
  const vehicleId = vehicle.id;
  const hasTriage = CheckFeatureFlag("Triage");
  const { saveHandlers: vehicleSaveHandlers } = useDefaultCRUDHandlers(t("Vehicle"));
  const { addNotification } = useNotification();
  const { update: updateVehicle } = useVehiclesMutations();

  const [checkInId, setCheckInId] = useState<string | undefined>(vehicle.checkInId ?? undefined);
  const [depollutionId, setDepollutionId] = useState<string | undefined>(vehicle.depollutionId ?? undefined);
  const [triageId, setTriageId] = useState<string | undefined>(vehicle.triageId ?? undefined);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [confirmArrivalOpen, setConfirmArrivalOpen] = useState(false);
  const [depollutionOpen, setDepollutionOpen] = useState(false);
  const [triageOpen, setTriageOpen] = useState(false);

  const { checkIn, checkInItems, checkInSteps, checkInDocuments, createCheckIn, saveCheckInItem, uploadCheckInItemDocuments, downloadCheckInItemDocument, viewSensitiveDocument, sensitiveDocumentAccess, deleteCheckInItemDocument } = useCheckIn(vehicleId, checkInId);
  const { jobs } = useJobsByVehicle(vehicleId);
  const { depollution, createDepollution } = useDepollution(depollutionId, vehicleId);
  const { triage, createTriage } = useTriage(triageId, vehicleId);

  useEffect(() => {
    setCheckInId(vehicle.checkInId ?? undefined);
  }, [vehicle.checkInId]);

  useEffect(() => {
    setDepollutionId(vehicle.depollutionId ?? undefined);
  }, [vehicle.depollutionId]);

  useEffect(() => {
    setTriageId(vehicle.triageId ?? undefined);
  }, [vehicle.triageId]);

  const checkInStepsData = checkInSteps.data?.data ?? [];
  const hasCheckInSteps = checkInStepsData.length > 0;
  const checkInItemsData = checkInItems.data?.data ?? [];
  const checkInDocumentsData = checkInDocuments.data?.data ?? [];
  const vehicleJobs = jobs.data?.data ?? [];
  const activeVehicleJobs = vehicleJobs.filter((job) => !job.deleted);

  const hasScheduledCollection = !!vehicle.scheduledCollectionOn;
  const isCollected = !!vehicle.collectedOn;
  const isCheckInComplete = !!checkIn.data?.data?.completed;
  const hasJobs = activeVehicleJobs.length > 0;
  const isDepollutionDone = !!depollution.data?.data?.completed;
  const isTriageDone = !!triage.data?.data?.completed;
  const isDismantlingDone = hasJobs && activeVehicleJobs.every(isJobDismantlingDone);

  const arrivalState: WorkflowStepState = isCollected ? "done" : hasScheduledCollection ? "in_progress" : "not_started";
  const checkInState: WorkflowStepState = isCheckInComplete ? "done" : isCollected ? "in_progress" : "not_started";
  const partsListState: WorkflowStepState = hasJobs ? "done" : "not_started";
  const depollutionState: WorkflowStepState = isDepollutionDone
    ? "done"
    : depollutionId
      ? "in_progress"
      : "not_started";
  const triageState: WorkflowStepState = isTriageDone
    ? "done"
    : triageId
      ? "in_progress"
      : "not_started";
  const dismantlingState: WorkflowStepState = isDismantlingDone ? "done" : (hasJobs && depollutionState === "done" ) ? "in_progress" : "not_started";

  const handleOpenCheckIn = () => {
    if (!vehicleId) return;

    if (checkInId) {
      setCheckInOpen(true);
      return;
    }

    createCheckIn.mutate(
      { vehicleId },
      {
        onSuccess: (res) => {
          setCheckInId(res.data.id);
          setCheckInOpen(true);
        },
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: t("Unable to start check-in"),
          });
        },
      }
    );
  };

  const handleConfirmArrival = () => {
    const collected = vehicle.scheduledCollectionOn
      ? DateTime.fromISO(vehicle.scheduledCollectionOn)
      : DateTime.now();

    const updatedVehicle: IVehicle = {
      ...vehicle,
      collectedOn: collected.toISODate(),
    };

    updateVehicle.mutate(updatedVehicle, {
      onSuccess: () => {
        vehicleSaveHandlers.onSuccess();
        setConfirmArrivalOpen(false);
      },
      onError: () => {
        vehicleSaveHandlers.onError();
      },
    });
  };

  const scrollToCreateJobs = () => {
    const target = document.getElementById(createJobsTargetId);
    if (!target) return;

    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleOpenDepollution = () => {
    if (depollutionId) {
      setDepollutionOpen(true);
      return;
    }

    createDepollution.mutate(
      { vehicleId },
      {
        onSuccess: (res) => {
          setDepollutionId(res.data.id);
          setDepollutionOpen(true);
        },
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: t("Unable to start depollution"),
          });
        },
      }
    );
  };

  const handleOpenTriage = () => {
    if (triageId) {
      setTriageOpen(true);
      return;
    }

    createTriage.mutate(
      { vehicleId },
      {
        onSuccess: (res) => {
          setTriageId(res.data.id);
          setTriageOpen(true);
        },
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: t("Unable to start triage"),
          });
        },
      }
    );
  };

  const workflowSteps: WorkflowStep[] = [
    {
      label: t("Arrival"),
      state: arrivalState,
      lineActive: isCollected,
      onClick: () => {
        if (isCollected) return;
        setConfirmArrivalOpen(true);
      },
      isClickable: !isCollected,
      instructions: t("The vehicle will usually be marked collected by a driver. Click to manually mark it as collected now."),
    },
    ...(hasCheckInSteps
      ? [{
        label: t("Check-In"),
        state: checkInState,
        lineActive: isCheckInComplete,
        onClick: handleOpenCheckIn,
        isClickable: !createCheckIn.isLoading,
        instructions: t("Follow the check-in steps and mark them all as done before continuing. Click to open check-in tasks."),
      } as WorkflowStep]
      : []),
    {
      label: t("Parts list"),
      state: partsListState,
      lineActive: hasJobs,
      onClick: scrollToCreateJobs,
      isClickable: !hasJobs,
      instructions: t("Create a job and parts list for this vehicle. Click to jump to Create Jobs."),
    },
    {
      label: t("Depollution"),
      state: depollutionState,
      lineActive: isDepollutionDone,
      onClick: handleOpenDepollution,
      isClickable: !createDepollution.isLoading,
      instructions: t("Complete depollution checks for this vehicle. Click to open depollution."),
    },
    ...(hasTriage
      ? [{
        label: t("Triage"),
        state: triageState,
        lineActive: isTriageDone,
        onClick: handleOpenTriage,
        isClickable: !createTriage.isLoading,
        instructions: t("Complete triage checks for this vehicle. Click to open triage."),
      } as WorkflowStep]
      : []),
    {
      label: t("Dismantling"),
      state: dismantlingState,
      instructions: t("All active jobs must be complete, or each job part must be removed or marked as damaged."),
    },
  ];

  return (
    <>
      <ConfirmModal
        open={confirmArrivalOpen}
        setOpen={setConfirmArrivalOpen}
        title={t("Mark as collected?")}
        message={t("Are you sure you want to mark this vehicle as collected?")}
        confirmButtonText={t("Mark collected")}
        onConfirm={handleConfirmArrival}
        isLoading={updateVehicle.isLoading}
      />

      <LeadCheckInModal
        open={checkInOpen}
        setOpen={setCheckInOpen}
        vehicleId={vehicleId}
        showTryCompleteLead={false}
        steps={checkInStepsData}
        items={checkInItemsData}
        documents={checkInDocumentsData}
        isLoading={checkInSteps.isLoading || (!!checkInId && checkInItems.isLoading)}
        saveCheckInItem={saveCheckInItem}
        uploadCheckInItemDocuments={uploadCheckInItemDocuments}
        downloadCheckInItemDocument={downloadCheckInItemDocument}
        viewSensitiveDocument={viewSensitiveDocument}
        sensitiveDocumentAccess={sensitiveDocumentAccess}
        deleteCheckInItemDocument={deleteCheckInItemDocument}
      />

      <Modal open={depollutionOpen} setOpen={setDepollutionOpen} width="max-w-6xl">
        <Depollution depollutionId={depollutionId} vehicleId={vehicleId} />
      </Modal>

      {hasTriage && (
        <Modal open={triageOpen} setOpen={setTriageOpen} width="max-w-6xl">
          <Triage triageId={triageId} vehicleId={vehicleId} />
        </Modal>
      )}

      <WorkflowStepper title={t("Vehicle Workflow")} steps={workflowSteps} />
    </>
  );
}
